import type { ComponentChildren } from "preact";
import { useRef } from "preact/hooks";

import { useAnchoredPosition } from "../hooks/useAnchoredPosition";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { useModalA11y } from "../hooks/useModalA11y";

export interface WeatherDetailOverlayProps {
  open: boolean;
  onClose: () => void;
  titleId: string;
  title: string;
  /** The trigger button/link the popover must anchor to (CD sign-off
   * carry-to-build item 3 — "anchor to the trigger, not the whole
   * component, so the caret visually points at what was tapped"). Only
   * consulted at `≥1024px`; the mobile sheet ignores it entirely (slides
   * up from the bottom, unanchored, matching every other sheet in this
   * app). */
  triggerRef: { current: HTMLElement | null };
  children: ComponentChildren;
}

const POPOVER_WIDTH = 340;
const POPOVER_MARGIN = 16;
const POPOVER_GAP = 10;

/**
 * Sprint-015 C1/C3 shared detail container
 * (weather-hub-ia-layout-2026-07-10.md §2/§4, CD sign-off 2026-07-10
 * carry-to-build item 3). `<1024px` -> `.heat-sheet` bottom sheet (new
 * component, visually sibling-shaped to `.assign-sheet` — the C0 spec's
 * §1 reuse manifest); `≥1024px` -> `.heat-popover` anchored to the
 * trigger button via `getBoundingClientRect()` so the caret points at
 * what was tapped, not the whole card. Both states share the SAME
 * `useModalA11y` focus-trap + Escape + focus-restore contract every other
 * overlay in this app already has (`AddPersonSheet`, `ConfirmDialog`,
 * `CalendarDetailPopover`); outside-click is the popover's own
 * transparent `.popover-scrim` (desktop) / the sheet's existing dimmed
 * backdrop (mobile) — both route through the hook's returned `close()`,
 * matching TD-012's "every dismiss path converges on one call site"
 * convention.
 *
 * Sprint-019 E5b (SA guardrail #3) — the anchor-to-trigger positioning
 * math below now lives in the shared `hooks/useAnchoredPosition.ts`
 * (extracted verbatim from this component's original inline `useEffect`,
 * behavior unchanged), so the new `InfoTooltip` desktop popover can reuse
 * the exact same viewport-collision logic instead of forking a second
 * copy.
 */
export function WeatherDetailOverlay({
  open,
  onClose,
  titleId,
  title,
  triggerRef,
  children,
}: WeatherDetailOverlayProps) {
  const isDesktop = useIsDesktop();
  const containerRef = useRef<HTMLDivElement>(null);
  const { close } = useModalA11y(containerRef, { active: open, onClose });
  const popoverStyle = useAnchoredPosition({
    active: open,
    isDesktop,
    triggerRef,
    width: POPOVER_WIDTH,
    margin: POPOVER_MARGIN,
    gap: POPOVER_GAP,
  });

  if (!open) return null;

  if (!isDesktop) {
    return (
      <>
        <div class="heat-sheet-backdrop" onClick={close} />
        <div
          ref={containerRef}
          class="heat-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div class="heat-sheet-handle" aria-hidden="true" />
          <div class="heat-sheet-head">
            <h2 class="heat-sheet-title" id={titleId}>
              {title}
            </h2>
            <button type="button" class="icon-btn" onClick={close} aria-label="Close" title="Close">
              ×
            </button>
          </div>
          {children}
        </div>
      </>
    );
  }

  return (
    <>
      <div class="popover-scrim" onClick={close} />
      <div
        ref={containerRef}
        class="heat-popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ ...popoverStyle }}
      >
        <div class="heat-popover-head">
          <h2 class="heat-popover-title" id={titleId}>
            {title}
          </h2>
          <button type="button" class="icon-btn" onClick={close} aria-label="Close" title="Close">
            ×
          </button>
        </div>
        {children}
      </div>
    </>
  );
}
